import { Box, CircularProgress, Skeleton } from "@mui/material";
import _ from "lodash";
import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PDFService from "src/services/pdfService";



import FormEngine from "@components/FormEngine/FormEngine";



import { getRecordId } from "@utils/helpers";



import { formNameMap } from "@constants/PatientFormConstants";



import { patientFormsService } from "../../../../services/patientForms";
import { CategoryData, CategoryType } from "../../../types/PatientFormTypes";
import { FormProgress, FormSection } from "../common";
import { PaperworkFieldWrapper } from "../common/FormFieldWrapper";
import { FormConfig, FormOrchestratorContextType, FormOrchestratorProviderProps, FormState, FormStep } from "./types";





// Create the context with undefined as default value
export const FormOrchestratorContext = createContext<FormOrchestratorContextType | undefined>(
  undefined,
);

/**
 * Initial form state
 */
const getInitialFormState = (patientId: string): FormState => ({
  patientId,
  formId: null,
  progressStep: "",
  completedSteps: [],
  currentStepIndex: -1,
  stepData: null,
  isLoading: true,
  isSubmitting: false,
  errors: {},
});

/**
 * FormOrchestrator Provider Component
 *
 * This component provides form orchestration functionality for multi-step forms.
 * It manages navigation between steps, tracks completion status, and handles form submission.
 * It works alongside FormEngine's internal form provider rather than competing with it.
 */
export const FormOrchestratorProvider: React.FC<FormOrchestratorProviderProps> = ({
  patientId,
  appointmentId,
  therapyId,
  formConfig,
  defaultValues,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [formState, setFormState] = useState<FormState>(getInitialFormState(patientId));

  const refreshWithoutParams = useCallback(
    (excludeParams: string[] = []) => {
      const searchParams = new URLSearchParams(location.search);
      const params = Array.from(searchParams.entries());
      const newParams = new URLSearchParams();
      for (const [key, value] of params) {
        if (!excludeParams.includes(key)) {
          newParams.set(key, value);
        }
      }
      history.push(`/forms?${newParams.toString()}`);
    },
    [history, location.search],
  );

  const getCurrentStep = useCallback((): FormStep | null => {
    if (formState.currentStepIndex === -1) return null;

    return formConfig.steps[formState.currentStepIndex];
  }, [formConfig.steps, formState.currentStepIndex]);

  const loadStepData = useCallback(
    async (stepIndex: number, _formState?: FormState, _formConfig?: FormConfig): Promise<CategoryData> => {
      const fs = _formState || formState;
      const fc = _formConfig || formConfig;
      const step = fc.steps[stepIndex];

      try {
        const background = await patientFormsService.getPatientBackground(patientId, [
          step.category,
        ]);
        const categoryData = _.mergeWith(defaultValues[step.category], background[step.category], (a, b) => {
          if (Array.isArray(b)) {
            return b;
          }
        });

        if (step.onLoadData) {
          await step.onLoadData(categoryData, fs, fc);
        }

        return categoryData;
      } catch (error) {
        console.error("Error loading step data:", error);

        return defaultValues[step.category];
      }
    },
    [formConfig.steps, patientId, defaultValues],
  );

  useEffect(() => {
    const initializeForm = async () => {
      try {
        setFormState((prev) => ({ ...prev, isLoading: true, stepData: null }));

        const searchParams = new URLSearchParams(location.search);
        const formId = searchParams.get("formId");
        const requestedStepId = searchParams.get("step");

        // Check for existing form
        const existingForm = formId
          ? await patientFormsService.getPatientFormById(formId)
          : await patientFormsService.getPatientForm(patientId, formConfig.formType, {
              appointmentId,
              therapyId,
            });

        if (formId && !existingForm) {
          // If there's a formId but no existing form, throw an error
          console.error(`Form with id=${formId} not found. Reloading without formId`);
          refreshWithoutParams(["formId"]);
        }

        if (existingForm) {
          if (existingForm.type !== formConfig.formType || existingForm.patientId !== patientId) {
            console.error(`Loaded form is invalid. Retrying...`);
            refreshWithoutParams(["formId"]);
            return;
          }

          const progressStep = existingForm.progress;
          const completedSteps =
            progressStep === "completed"
              ? formConfig.steps.map((step) => step.id)
              : formConfig.steps.map((step) => step.id).slice(0, getStepIndex(progressStep));

          console.log("orchestrator completedSteps", completedSteps);

          const allowSkip = formConfig.allowSkipSteps;
          const isCompleted = completedSteps.includes(requestedStepId || "");
          const isProgressStep = requestedStepId === progressStep;

          if (!allowSkip && !isCompleted && !isProgressStep) {
            console.error(`Trying to access invalid step. Redirecting to progress step...`);
            goToStep(getStepIndex(progressStep));
            return;
          }

          const currentStepIndex = getStepIndex(requestedStepId || existingForm.progress);

          const tempFormState = {
            ...formState,
            formId: getRecordId(existingForm),
            progressStep: existingForm.progress,
            currentStepIndex,
            completedSteps,
          };

          const stepData = await loadStepData(currentStepIndex, tempFormState, formConfig);
          setFormState((prev) => ({
            ...prev,
            ...tempFormState,
            stepData,
            isLoading: false,
            isSubmitting: false,
          }));
        } else {
          // Create new form session
          const newForm = await patientFormsService.createForm(
            patientId,
            formConfig.formType,
            formConfig.steps[0].id,
            { appointmentId, therapyId },
          );

          // Load data for the first step
          const stepData = await loadStepData(0);

          setFormState((prev) => ({
            ...prev,
            formId: getRecordId(newForm),
            progressStep: newForm.progress,
            currentStepIndex: 0,
            completedSteps: [],
            stepData,
            isLoading: false,
            isSubmitting: false,
          }));
        }
      } catch (error) {
        console.error("Error initializing form:", error);
        setFormState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            general: "Failed to initialize form. Please try again.",
          },
          isLoading: false,
          isSubmitting: false,
        }));
      }
    };

    initializeForm();
  }, [patientId, formConfig, location.search, loadStepData]);

  const getStepIndex = useCallback(
    (stepId: string) => {
      return Math.max(
        0,
        formConfig.steps.findIndex((step) => step.id === stepId),
      );
    },
    [formConfig.steps],
  );

  // Navigation methods
  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < formConfig.steps.length) {
        const queryParams = new URLSearchParams();

        queryParams.set("formType", formConfig.formType);
        queryParams.set("step", formConfig.steps[stepIndex].id);
        if (formState.formId) queryParams.set("formId", formState.formId);
        if (appointmentId) queryParams.set("appointmentId", appointmentId);
        if (therapyId) queryParams.set("therapyId", therapyId);

        history.push(`/forms?${queryParams.toString()}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [
      formConfig.steps.length,
      formConfig.formType,
      history,
      formState.formId,
      appointmentId,
      therapyId,
    ],
  );

  const goToNextStep = useCallback(() => {
    // Details form stays on the same page, so scroll to top
    if (formConfig.allowSkipSteps) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (formState.currentStepIndex < formConfig.steps.length - 1) {
      goToStep(formState.currentStepIndex + 1);
    } else {
      submitForm().then((success) => {
        if (success) history.push(`/forms/complete?formId=${formState.formId}`);
      });
    }
  }, [
    formState.currentStepIndex,
    formConfig.steps.length,
    formConfig.formType,
    goToStep,
    history,
    formState.formId,
  ]);

  const goToPreviousStep = useCallback(() => {
    if (formState.currentStepIndex > 0) {
      goToStep(formState.currentStepIndex - 1);
    }
  }, [formState.currentStepIndex, goToStep]);

  const isStepCompleted = useCallback(
    (step: FormStep) =>
      formState.completedSteps.includes(typeof step === "string" ? step : step.id),
    [formState.completedSteps],
  );

  const saveCategoryData = useCallback(
    async (data: CategoryData, category?: CategoryType) => {
      try {
        setFormState((prev) => ({
          ...prev,
          isLoading: true,
          stepData: data,
          errors: {},
        }));

        const currentCategory = category || getCurrentStep()?.category;
        if (!currentCategory) throw new Error("No category found");

        await patientFormsService.updateCategory(patientId, currentCategory, data);

        return true;
      } catch (error) {
        console.error("Error saving category data:", error);
        setFormState((prev) => ({
          ...prev,
          errors: {
            ...prev.errors,
            general: "Failed to save form data. Please try again.",
          },
        }));

        return false;
      } finally {
        setFormState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    },
    [patientId, formState.formId, formConfig.steps, formState.currentStepIndex, getCurrentStep],
  );

  const setStepData = useCallback((data: CategoryData) => {
    setFormState((prev) => ({
      ...prev,
      stepData: data,
    }));
  }, []);

  const handleSubmit = async (data: any) => {
    console.log("handleSubmit", data);
    if (formState.isSubmitting) return;

    setFormState((prev) => ({
      ...prev,
      isSubmitting: true,
      errors: {},
    }));

    try {
      const currentStep = getCurrentStep();
      if (!currentStep) throw new Error("No current step found");

      if (currentStep.onSubmit) {
        await currentStep.onSubmit(data, formState, formConfig);
      }

      await patientFormsService.updateCategory(patientId, currentStep.category, data);

      const progressStepIndex = formConfig.steps.findIndex(
        (step) => step.id === formState.progressStep,
      );
      const furthestStepIndex = Math.max(progressStepIndex, formState.currentStepIndex + 1);

      if (formState.progressStep === "completed") {
        // Do nothing
      } else if (formState.formId && furthestStepIndex === formConfig.steps.length) {
        await patientFormsService.updateFormProgress(formState.formId, "completed");
      } else if (formState.formId && furthestStepIndex < formConfig.steps.length) {
        const newProgressStep = formConfig.steps[furthestStepIndex];
        await patientFormsService.updateFormProgress(formState.formId, newProgressStep.id);
      }

      goToNextStep();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setFormState((prev) => ({
        ...prev,
        isLoading: false,
        isSubmitting: false,
      }));
    }
  };

  const submitForm = useCallback(async () => {
    if (formState.isSubmitting) return false;

    try {
      setFormState((prev) => ({
        ...prev,
        isSubmitting: true,
        errors: {},
      }));

      // Submit the form (data gathered on backend)
      const response = await patientFormsService.submitForm(
        formConfig.formType,
        patientId,
        formState.formId!,
        { appointmentId, therapyId },
      );

      // Generate PDF with the submitted form data
      const pdf = await PDFService.generatePDF(formConfig.formType, patientId, response.formData);
      await PDFService.savePDF(pdf, {
        title: formNameMap[formConfig.formType],
        userId: patientId,
        fileCategory: "OTHER",
        usedFor: "patient form",
      });

      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
      }));

      return true;
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        errors: {
          ...prev.errors,
          general: "Failed to submit form. Please try again.",
        },
      }));
      return false;
    }
  }, [
    patientId,
    formConfig.formType,
    formState.formId,
    formState.isSubmitting,
    appointmentId,
    therapyId,
  ]);

  // Create context value
  const contextValue = useMemo(
    () => ({
      formState,
      formConfig,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      isStepCompleted,
      getCurrentStep,
      loadStepData,
      saveCategoryData,
      setStepData,
      submitForm,
    }),
    [
      formState,
      formConfig,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      isStepCompleted,
      getCurrentStep,
      loadStepData,
      saveCategoryData,
      setStepData,
      submitForm,
    ],
  );

  const currentStep = getCurrentStep();

  return (
    <FormOrchestratorContext.Provider value={contextValue}>
      <FormProgress />
      <FormSection
        title={
          currentStep?.title || <Skeleton variant="text" height={60} width={300} animation="wave" />
        }
      >
        {formState.isLoading || !formState.stepData || !currentStep ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Skeleton variant="rectangular" height={400} />
          </Box>
        ) : (
          <FormEngine
            schema={currentStep.schema}
            validation={currentStep.validation}
            data={formState.stepData}
            onSubmit={handleSubmit}
            submitBtnTitle={formConfig.allowSkipSteps ? "Save" : "Save & Continue"}
            onCancel={() => goToPreviousStep()} // TODO: Disable/hide on Details form
            loading={formState.isSubmitting}
            cancelBtnProps={{
              disabled: formState.currentStepIndex === 0,
            }}
            cancelBtnTitle="Back"
            format={{
              fullWidthButtons: false,
              buttonsDirection: "row-reverse",
              spacing: 2,
            }}
            FieldWrapper={PaperworkFieldWrapper}
          />
        )}
      </FormSection>
    </FormOrchestratorContext.Provider>
  );
};

export default FormOrchestratorProvider;